import { useMemo } from 'react';
import { FaFileExport } from 'react-icons/fa';
import {
  MdCheck,
  MdClear,
  MdCloudDownload,
  MdDelete,
  MdFolder,
  MdMoreVert,
  MdShare,
  MdUndo,
} from 'react-icons/md';
import { Menu } from '@headlessui/react';
import {
  Button,
  DropdownDivider,
  DropdownGroup,
  DropdownItem,
  DropdownItems,
  Icon,
  usePopperDropdown,
} from '@nbirl/nbi-common-ui-libs';
import { Portal } from '@reach/portal';

import { useCurrentUser, useDownload } from '../../../../../../shared/context';
import { Permission } from '../../../../../../shared/enum';
import { useErrorToast } from '../../../../../../shared/hooks';
import { DocumentStatus, SecureDocument } from '../../../models';
import { useChangeDocumentStatusMutation } from '../../../queries';
import { getStatusLabel } from '../../../utils';

type DocumentDropdownMenuProps = {
  selectedDocument: SecureDocument;
  parentFolderId: string;
  showInFolderItem: boolean;
  isDeleteDocumentPending: boolean;
  creatorId: string;
  documentStatus?: DocumentStatus;
  onDocumentStatusUpdateStart: (id: string) => void;
  onDocumentStatusUpdateFinish: (id: string) => void;
  onShareDocument: () => void;
  onFolderChange: (id: string) => void;
  onShowMoveDocumentModal: () => void;
  onShowDeleteDocumentModal: () => void;
};

export const DocumentDropdownMenu: React.FC<DocumentDropdownMenuProps> = ({
  selectedDocument,
  parentFolderId,
  showInFolderItem,
  isDeleteDocumentPending,
  creatorId,
  documentStatus,
  onDocumentStatusUpdateStart,
  onDocumentStatusUpdateFinish,
  onShareDocument,
  onFolderChange,
  onShowMoveDocumentModal,
  onShowDeleteDocumentModal,
}) => {
  const { permissions, id: currentUserId } = useCurrentUser();
  const { showErrorToast } = useErrorToast();
  const changeStatusMutation = useChangeDocumentStatusMutation();
  const { download, isDownloading } = useDownload();
  const { id: documentId } = selectedDocument;
  const { referenceElementProps, popperElementProps } = usePopperDropdown({
    placement: 'right-start',
  });

  const availableStatuses = useMemo(() => {
    let statuses: DocumentStatus[] = [];

    switch (documentStatus) {
      case 'PENDING':
        statuses = ['PUBLISHED', 'REJECTED'];
        break;
      case 'PUBLISHED':
        statuses = ['PENDING'];
        break;
      case 'REJECTED':
        statuses = ['PENDING'];
        break;
    }

    const getStatusIcon = (status: DocumentStatus) => {
      switch (status) {
        case 'PUBLISHED':
          return <MdCheck className="text-gray-300" />;
        case 'PENDING':
          return <MdUndo className="text-gray-300" />;
        default:
          return <MdClear className="text-gray-300" />;
      }
    };

    return statuses.map((status) => ({
      status,
      icon: getStatusIcon(status),
    }));
  }, [documentStatus]);

  const handleOnStatusChange = async (status: DocumentStatus) => {
    onDocumentStatusUpdateStart(documentId);

    try {
      await changeStatusMutation.mutateAsync({
        status,
        documentId,
        parentFolderId,
      });
    } catch {
      showErrorToast('Document status could not be changed. Please try again.');
    } finally {
      onDocumentStatusUpdateFinish(documentId);
    }
  };

  const handleOnDownload = () =>
    download({ documentIds: [documentId], folderIds: [] });

  return (
    <Menu>
      <Menu.Button
        as={Button}
        variant="ghost"
        aria-label="Select action"
        size="sm"
        onlyIcon
        {...referenceElementProps}
      >
        <Icon size="20" icon={<MdMoreVert />} />
      </Menu.Button>
      <Portal>
        <DropdownItems {...popperElementProps}>
          {permissions.includes(Permission.ChangeDocumentStatus) && (
            <>
              <DropdownGroup title="Change status">
                {availableStatuses.map(({ status, icon }) => (
                  <DropdownItem
                    key={status}
                    icon={<Icon icon={icon} size="18" />}
                    onClick={() => handleOnStatusChange(status)}
                    disabled={changeStatusMutation.isLoading}
                  >
                    {getStatusLabel(status)}
                  </DropdownItem>
                ))}
              </DropdownGroup>
              <DropdownDivider />
            </>
          )}
          <DropdownItem
            icon={
              <Icon size="18" icon={<MdShare className="text-gray-300" />} />
            }
            onClick={onShareDocument}
          >
            Share
          </DropdownItem>
          <DropdownItem
            icon={<MdCloudDownload className="text-gray-300" />}
            onClick={handleOnDownload}
            disabled={isDownloading}
          >
            Download
          </DropdownItem>

          {showInFolderItem && parentFolderId && (
            <DropdownItem
              icon={
                <Icon size="18" icon={<MdFolder className="text-gray-300" />} />
              }
              onClick={() => onFolderChange(parentFolderId)}
            >
              Show in folder
            </DropdownItem>
          )}

          {permissions.includes(Permission.ManageFolders) && (
            <DropdownItem
              icon={
                <Icon
                  size="18"
                  icon={<FaFileExport className="text-gray-300" />}
                />
              }
              onClick={onShowMoveDocumentModal}
            >
              Move to
            </DropdownItem>
          )}

          {(permissions.includes(Permission.DeleteDocument) ||
            currentUserId === creatorId) && (
            <>
              <DropdownDivider />
              <DropdownItem
                icon={<Icon size="18" icon={<MdDelete />} />}
                variant="danger"
                disabled={isDeleteDocumentPending}
                onClick={onShowDeleteDocumentModal}
              >
                Delete document
              </DropdownItem>
            </>
          )}
        </DropdownItems>
      </Portal>
    </Menu>
  );
};
